import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, Heart } from 'lucide-react';
import { TESTIMONIALS_DATA } from '../data/testimonialsData';

export const UserReviewsSection: React.FC = () => {
  const [startIndex, setStartIndex] = useState(0);
  const itemsPerPage = 3;

  const handlePrev = () => {
    setStartIndex((prev) => (prev === 0 ? Math.max(0, TESTIMONIALS_DATA.length - itemsPerPage) : prev - 1));
  };

  const handleNext = () => {
    setStartIndex((prev) => (prev + itemsPerPage >= TESTIMONIALS_DATA.length ? 0 : prev + 1));
  };

  const visibleTestimonials = TESTIMONIALS_DATA.slice(startIndex, startIndex + itemsPerPage);
  // If fewer than 3 at the end of slice, fill from start
  const displayItems = visibleTestimonials.length < itemsPerPage
    ? [...visibleTestimonials, ...TESTIMONIALS_DATA.slice(0, itemsPerPage - visibleTestimonials.length)]
    : visibleTestimonials;

  return (
    <section id="reviews-section" className="py-16 sm:py-20 bg-slate-50/70 border-t border-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Title matching screenshot */}
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            What Our Users Say <span className="text-pink-500 inline-block">💕</span>
          </h2>
          <p className="mt-2 text-slate-500 text-sm sm:text-base">
            Verified feedback from genuine clients who hired companions for special occasions.
          </p>
        </div>

        {/* Carousel Container with Prev/Next buttons matching screenshot */}
        <div className="relative flex items-center gap-2 sm:gap-4">
          
          {/* Left Arrow Button */}
          <button
            id="review-prev-btn"
            onClick={handlePrev}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-50 hover:bg-pink-100 border border-pink-200 text-pink-600 flex items-center justify-center shrink-0 transition-all hover:scale-105 active:scale-95 shadow-xs cursor-pointer"
            aria-label="Previous reviews"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {/* Testimonial Cards Grid */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6">
            {displayItems.map((testi) => (
              <div
                key={testi.id}
                id={`testimonial-card-${testi.id}`}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg hover:border-pink-200 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  {/* Star ratings */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testi.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-pink-500 text-pink-500" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-slate-700 text-sm leading-relaxed mb-6 font-medium">
                    "{testi.quote}"
                  </p>
                </div>

                {/* User avatar and name matching screenshot */}
                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <img
                    src={testi.avatar}
                    alt={testi.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-pink-100"
                    referrerPolicy="no-referrer"
                  />
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">
                      – {testi.name}
                    </h4>
                    {testi.role && (
                      <p className="text-[11px] text-slate-400">{testi.role}</p>
                    )}
                  </div>
                </div>

              </div>
            ))}
          </div>

          {/* Right Arrow Button */}
          <button
            id="review-next-btn"
            onClick={handleNext}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-50 hover:bg-pink-100 border border-pink-200 text-pink-600 flex items-center justify-center shrink-0 transition-all hover:scale-105 active:scale-95 shadow-xs cursor-pointer"
            aria-label="Next reviews"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

        </div>

      </div>
    </section>
  );
};
